import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useStores } from "../stores";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PrimaryButton from "../components/PrimaryButton";
import InputField from "../components/InputField";
import FeatureItem from "../components/FeatureItem";
import HeroSkeleton from "../components/HeroSkeleton";
import { MESSAGES, formatMessage } from "../utils/messages";

interface BrokerInfo {
  name: string;
  title: string;
  creci: string;
  bioParagraphs: string[];
  stats: {
    sales: string;
    volume: string;
    experience: string;
  };
}

interface ContactInfo {
  phone: string;
  email: string;
  address: string;
}

interface HomeData {
  broker: BrokerInfo;
  contact: ContactInfo;
}

const HOME_DATA: HomeData = {
  broker: {
    name: "José Américo Mendicino",
    title: "ESPECIALISTA EM IMÓVEIS EM BELO HORIZONTE",
    creci: "CRECI-MG 46059",
    bioParagraphs: [
      "Especialista em imóveis de qualidade em Belo Horizonte, com sólida experiência no mercado imobiliário e formação superior em Ciências Contábeis. Uno a expertise da corretagem de imóveis à visão analítica e estratégica da contabilidade para avaliar cada oportunidade com critério, segurança e precisão.",
      "Trabalho com imóveis selecionados, de qualidade e com potencial de valorização, oferecendo uma consultoria que vai além da intermediação. Meu compromisso é proporcionar negociações sólidas, transparentes e seguras, ajudando cada cliente a encontrar um imóvel que realmente faça sentido para seus objetivos.",
    ],
    stats: {
      sales: "250+ Vendas de Luxo",
      volume: "R$ 1.2B+ Em Negócios",
      experience: "25 Anos De Atuação",
    },
  },
  contact: {
    phone: "+55 (31) 99691-5436",
    email: "contabilidademendicino@gmail.com",
    address: "Belo Horizonte, MG",
  },
};

const TIPO_TO_URL: Record<string, string> = {
  casa: "casas",
  apto: "apartamentos",
  lote: "lotes",
  loja: "lojas",
  sala: "salas",
  outros: "outros",
};

const TIPO_TO_DISPLAY: Record<string, string> = {
  casa: "Casa",
  apto: "Apartamento",
  loja: "Loja",
  lote: "Lote",
  sala: "Sala",
  outros: "Outros",
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { propertyStore, filterStore, contactStore } = useStores();
  const { featuredProperties, isLoading } = propertyStore;
  const { neighborhoods: bairros, cities } = filterStore;

  const heroProperty = featuredProperties[0];

  useEffect(() => {
    propertyStore.loadFeatured();
    filterStore.loadNeighborhoods();
    filterStore.loadCities();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [filterCity, setFilterCity] = useState("");
  const [filterBairro, setFilterBairro] = useState("");
  const [filterType, setFilterType] = useState("casa");
  const [filterRooms, setFilterRooms] = useState("1+");
  const [filterMaxPrice, setFilterMaxPrice] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [contactSent, setContactSent] = useState(false);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    const rooms = filterRooms.replace("+", "");
    if (rooms && rooms !== "1") params.set("quartos", rooms);
    if (filterMaxPrice) params.set("pmax", filterMaxPrice);
    if (filterCity) params.set("cidade", filterCity);
    if (filterBairro) params.set("bairro", filterBairro);
    const tipoUrl = TIPO_TO_URL[filterType] || filterType;
    const qs = params.toString();
    navigate(`/imoveis/${tipoUrl}${qs ? `?${qs}` : ""}`);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim() || formData.name.trim().length < 2)
      errors.name = "Informe seu nome completo";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()))
      errors.email = "Informe um e-mail válido";
    const phoneDigits = formData.phone.replace(/\D/g, "");
    if (!formData.phone.trim() || phoneDigits.length < 8)
      errors.phone = "Informe um telefone com pelo menos 8 dígitos";
    if (!formData.message.trim())
      errors.message = "Escreva uma mensagem";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactSent(false);
    if (!validateForm()) return;
    const ok = await contactStore.sendLead(formData);
    if (ok) {
      setContactSent(true);
      setFormData({ name: "", email: "", phone: "", message: "" });
      setFormErrors({});
    }
  };

  return (
    <div style={{ overflowX: "hidden" }}>
      <Header />

      {isLoading ? (
        <HeroSkeleton />
      ) : heroProperty ? (
        <header
          className="position-relative text-white"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45)), url(${heroProperty.images[heroProperty.featuredIndex]})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            padding: "120px 0 160px 0",
          }}
        >
          <div className="container">
            <div className="row">
              <div className="col-lg-7">
                <span
                  className="badge text-uppercase px-3 py-2 mb-3 fw-bold"
                  style={{ backgroundColor: "#2A9D8F", fontSize: "0.75rem" }}
                >
                  DESTAQUE DA SEMANA
                </span>
                <h1 className="display-4 fw-bold mb-3">
                  {TIPO_TO_DISPLAY[heroProperty.type] || heroProperty.type} -{" "}
                  {heroProperty.location.neighborhood}
                </h1>
                <p className="lead opacity-90 mb-4 fs-6 text-light col-md-10">
                  {heroProperty.description.split("\n")[0]}
                </p>
                <div className="d-flex flex-wrap gap-3">
                  <PrimaryButton
                    variant="light"
                    href={`/imovel/${heroProperty.code}`}
                  >
                    Ver Detalhes
                  </PrimaryButton>
                  <PrimaryButton
                    variant="accent"
                    icon="far fa-calendar-alt"
                    onClick={() =>
                      contactStore.openWhatsApp(
                        formatMessage(MESSAGES.WHATSAPP_SCHEDULE, {
                          type:
                            TIPO_TO_DISPLAY[heroProperty.type] ||
                            heroProperty.type,
                          neighborhood: heroProperty.location.neighborhood,
                          code: heroProperty.code,
                        }),
                      )
                    }
                  >
                    Agendar Visita
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </header>
      ) : null}

      <div className="container" style={{ marginTop: "-60px" }}>
        <div
          className="bg-white rounded-3 shadow p-4 mx-auto position-relative"
          style={{ zIndex: 2 }}
        >
          <form
            className="row g-3 align-items-end"
            onSubmit={handleSearchSubmit}
          >
            <div className="col-12 col-md-6 col-lg-2">
              <InputField
                icon="fas fa-map-marker-alt"
                label="Cidade"
                type="select"
                value={filterCity}
                onChange={(e) => setFilterCity(e.target.value)}
                options={[
                  { value: "", label: "Todas as Cidades" },
                  ...cities.map((c) => ({ value: c, label: c })),
                ]}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <InputField
                icon="fas fa-city"
                label="Bairro"
                type="select"
                value={filterBairro}
                onChange={(e) => setFilterBairro(e.target.value)}
                options={[
                  { value: "", label: "Todos os Bairros" },
                  ...bairros.map((b) => ({ value: b, label: b })),
                ]}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <InputField
                icon="fas fa-building"
                label="Tipo de Imóvel"
                type="select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                options={[
                  { value: "casa", label: "Casa" },
                  { value: "apto", label: "Apartamento" },
                  { value: "lote", label: "Lote" },
                  { value: "loja", label: "Loja" },
                  { value: "sala", label: "Sala" },
                  { value: "outros", label: "Outros" },
                ]}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <InputField
                icon="fas fa-bed"
                label="Quartos"
                type="select"
                value={filterRooms}
                onChange={(e) => setFilterRooms(e.target.value)}
                options={[
                  { value: "1+", label: "1+" },
                  { value: "2+", label: "2+" },
                  { value: "3+", label: "3+" },
                  { value: "4+", label: "4+" },
                ]}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <InputField
                icon="fas fa-dollar-sign"
                label="Preço Máximo"
                placeholder="R$ 0,00"
                value={filterMaxPrice}
                onChange={(e) => setFilterMaxPrice(e.target.value)}
              />
            </div>
            <div className="col-12 col-lg-2">
              <PrimaryButton
                variant="primary"
                fullWidth
                type="submit"
                icon="fas fa-search"
              >
                Buscar Imóveis
              </PrimaryButton>
            </div>
          </form>
        </div>
      </div>

      <section className="py-5 my-5">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-5 position-relative">
              <img
                src="https://d3ckyzlks8vzjp.cloudfront.net/site-images/perfil.png"
                alt={HOME_DATA.broker.name}
                className="img-fluid rounded-3 shadow position-relative w-100"
                style={{ objectFit: "cover", height: "420px" }}
              />
            </div>
            <div className="col-lg-7">
              <span className="text-uppercase fw-bold text-muted small d-block mb-2">
                {HOME_DATA.broker.title}
              </span>
              <h2 className="fw-bold mb-4" style={{ color: "#0B2545" }}>
                {HOME_DATA.broker.name}
              </h2>

              {HOME_DATA.broker.bioParagraphs.map((para, idx) => (
                <p key={idx} className="text-muted lh-lg mb-3">
                  {para}
                </p>
              ))}

              {/* <div className="row g-4 mt-4 py-3 border-top border-bottom my-4">
                <div className="col-4">
                  <h4 className="fw-bold m-0" style={{ color: "#0B2545" }}>
                    250+
                  </h4>
                  <small
                    className="text-muted text-uppercase"
                    style={{ fontSize: "0.65rem", fontWeight: 700 }}
                  >
                    Vendas de Luxo
                  </small>
                </div>
                <div className="col-4">
                  <h4 className="fw-bold m-0" style={{ color: "#0B2545" }}>
                    R$ 1.2B+
                  </h4>
                  <small
                    className="text-muted text-uppercase"
                    style={{ fontSize: "0.65rem", fontWeight: 700 }}
                  >
                    Em Negócios
                  </small>
                </div>
                <div className="col-4">
                  <h4 className="fw-bold m-0" style={{ color: "#0B2545" }}>
                    25 Anos
                  </h4>
                  <small
                    className="text-muted text-uppercase"
                    style={{ fontSize: "0.65rem", fontWeight: 700 }}
                  >
                    De Atuação
                  </small>
                </div>
              </div> */}

              {/* <a href="/sobre" className="text-decoration-none fw-bold d-flex align-items-center gap-2" style={{ color: '#13315C' }}>
                Conheça minha trajetória <i className="fas fa-arrow-right"></i>
              </a> */}
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-white border-top">
        <div className="container py-4">
          <div className="row g-5">
            <div className="col-lg-5">
              <h2
                className="display-6 fw-bold mb-3"
                style={{ color: "#0B2545" }}
              >
                Vamos encontrar seu próximo destino?
              </h2>
              <p className="text-muted mb-5">
                Preencha o formulário e entraremos em contato em menos de 24
                horas para uma consultoria.
              </p>

              <div className="d-flex flex-column gap-4">
                <FeatureItem
                  icon="fas fa-phone-alt"
                  title="Atendimento Direto"
                  description={HOME_DATA.contact.phone}
                />
                <FeatureItem
                  icon="fas fa-envelope"
                  title="Email Profissional"
                  description={HOME_DATA.contact.email}
                />
                <FeatureItem
                  icon="fas fa-map-marker-alt"
                  title="Região de Atuação"
                  description={HOME_DATA.contact.address}
                />
              </div>
            </div>

            <div className="col-lg-7">
              <div className="p-4 p-md-5 rounded-3 shadow-sm border bg-white">
                {contactSent && (
                  <div className="alert alert-success py-2 small mb-3">
                    <i className="fas fa-check-circle me-2"></i>
                    Mensagem enviada com sucesso! Entraremos em contato em
                    breve.
                  </div>
                )}
                {contactStore.lastError && (
                  <div className="alert alert-danger py-2 small mb-3">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {contactStore.lastError}
                  </div>
                )}
                <form className="row g-3" onSubmit={handleContactSubmit}>
                  <div className="col-12">
                    <label className="form-label small fw-semibold text-muted">
                      Nome Completo
                    </label>
                    <input
                      type="text"
                      className={`form-control py-2 ${formErrors.name ? "is-invalid" : ""}`}
                      placeholder="Ex: João da Silva"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        if (formErrors.name) setFormErrors((p) => ({ ...p, name: "" }));
                      }}
                    />
                    {formErrors.name && (
                      <div className="invalid-feedback">{formErrors.name}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold text-muted">
                      E-mail
                    </label>
                    <input
                      type="email"
                      className={`form-control py-2 ${formErrors.email ? "is-invalid" : ""}`}
                      placeholder="seu@email.com"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (formErrors.email) setFormErrors((p) => ({ ...p, email: "" }));
                      }}
                    />
                    {formErrors.email && (
                      <div className="invalid-feedback">{formErrors.email}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold text-muted">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      className={`form-control py-2 ${formErrors.phone ? "is-invalid" : ""}`}
                      placeholder="(31) 99999-9999"
                      value={formData.phone}
                      onChange={(e) => {
                        setFormData({ ...formData, phone: e.target.value });
                        if (formErrors.phone) setFormErrors((p) => ({ ...p, phone: "" }));
                      }}
                    />
                    {formErrors.phone && (
                      <div className="invalid-feedback">{formErrors.phone}</div>
                    )}
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold text-muted">
                      Mensagem
                    </label>
                    <textarea
                      className={`form-control py-2 ${formErrors.message ? "is-invalid" : ""}`}
                      rows={4}
                      placeholder="Como podemos ajudar você hoje?"
                      value={formData.message}
                      onChange={(e) => {
                        setFormData({ ...formData, message: e.target.value });
                        if (formErrors.message) setFormErrors((p) => ({ ...p, message: "" }));
                      }}
                    ></textarea>
                    {formErrors.message && (
                      <div className="invalid-feedback">{formErrors.message}</div>
                    )}
                  </div>
                  <div className="col-12 mt-4">
                    <PrimaryButton
                      variant="primary"
                      fullWidth
                      type="submit"
                      disabled={contactStore.isSending}
                    >
                      {contactStore.isSending
                        ? "Enviando..."
                        : "Enviar Mensagem"}
                    </PrimaryButton>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default observer(Home);
